import { useState } from "react"

import { Skeleton } from "antd"
import { useTranslation } from "react-i18next"

import Icon from "../../../../../Icon"
import AppUtils from "../../../../../Utils/utils"
import { CODES_VIEW } from "../../constants/constants"

import styles from "./codes-panel.module.less"

// One code, with a copy button that copies just this value (BLOCK 5.7).
const CodeChip = ({ value }) => {
  const [isCopied, setIsCopied] = useState(false)

  const copy = () => {
    AppUtils.copyToClipboard(value)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 1500)
  }

  return (
    <div className={styles.chip}>
      <span className={styles.chipValue}>{value}</span>
      <button
        type="button"
        className={styles.chipCopy}
        onClick={copy}
        aria-label={`Copy ${value}`}
        data-testid={`deflect-business-code-copy-${value}`}
      >
        {isCopied ? <Icon type="check" /> : <Icon type="copy" />}
      </button>
    </div>
  )
}

const CodeGroup = ({ title, values, t }) =>
  values.length ? (
    <div className={styles.group}>
      {/* BIN and CAID share one view but are different code types, so each keeps
          its own heading — a flat grid would make them indistinguishable. */}
      {title ? <span className={styles.groupTitle}>{t(title)}</span> : null}
      <div className={styles.grid}>
        {values.map((value) => (
          <CodeChip key={value} value={value} />
        ))}
      </div>
    </div>
  ) : null

// The right-hand panel while a codes view is open. Read-only: codes are added
// only through the program card's footer button (BLOCK 5.7 / BLOCK 4.2).
const CodesPanel = ({ view, codes }) => {
  const { t } = useTranslation()

  const isArn = view === CODES_VIEW.ARN
  const groups = isArn
    ? [{ title: null, values: codes?.arn || [] }]
    : [
        { title: "BIN codes", values: codes?.bin || [] },
        { title: "CAID codes", values: codes?.caid || [] }
      ]

  const isEmpty = groups.every((group) => !group.values.length)

  if (codes?.error) return <div className={styles.empty}>{codes.error}</div>

  // Checked before the empty state: the lists are cleared while a fetch is in
  // flight, so without this a fresh view would flash "no codes found" first.
  if (codes?.fetching && isEmpty) {
    return (
      <div className={styles.panel}>
        <div className={styles.grid}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton.Input key={i} active block className={styles.chipSkeleton} />
          ))}
        </div>
      </div>
    )
  }

  // BLOCK 5.9 — a view the BA has no codes for still renders, with its own empty
  // state. (A type it has none of at all is not in the cycle to begin with.)
  if (isEmpty) {
    return (
      <div className={styles.empty} data-testid="deflect-business-codes-empty">
        {isArn ? t("No ARN codes found") : t("No BIN & CAID codes found")}
      </div>
    )
  }

  return (
    <div className={styles.panel} data-testid={`deflect-business-codes-${view}`}>
      {groups.map((group, i) => (
        <CodeGroup key={group.title || i} title={group.title} values={group.values} t={t} />
      ))}
    </div>
  )
}

export default CodesPanel
